import oktaService from '@/services/OktaService';
import { backendApiUrl, resolveConfigValue } from '@/lib/config';

export type ThreeDxProjectInfo = {
    projectName?: string;
    projectID?: string;
    createdAt?: string;
    createdBy?: string;
    updatedAt?: string;
    projectDescription?: string;
    project_users?: string[];
    [key: string]: unknown;
};

export type ThreeDxProject = {
    dtrId?: string;
    dtrStatus?: string;
    project_info?: ThreeDxProjectInfo;
};

export type ThreeDxProjectResponse = {
    data: ThreeDxProject[];
};

const threeDxProjectImportUrl = `${backendApiUrl}/threedx/projects/import`;

const getRuntimeConfigValue = (key: string) => {
    const runtimeEnv = window.ENV as Record<string, string | undefined> | undefined;
    return runtimeEnv?.[key];
};

const getUserGpid = (profile: unknown) => {
    if (!profile || typeof profile !== 'object') return undefined;

    const claims = profile as Record<string, unknown>;
    const value = claims.gpid ?? claims.GPID ?? claims.uid ?? claims.sub ?? claims.email;
    return typeof value === 'string' ? value : undefined;
};

export type ThreeDxProjectRequestError = Error & { status?: number };

export const getThreeDxProject = async (dtrId: string): Promise<ThreeDxProjectResponse | null> => {
    const oktaUser = await oktaService.getUser();
    if (!oktaUser?.access_token) {
        throw new Error('No access token is available for the project request');
    }

    const response = await fetch(`${backendApiUrl}/threedx/projects/${encodeURIComponent(dtrId.trim())}`, {
        credentials: 'include',
        headers: {
            Authorization: `Bearer ${oktaUser.access_token}`,
        },
    });

    if (!response.ok) {
        let serverMessage: string | undefined;
        try {
            const errorBody = await response.json();
            serverMessage = typeof errorBody?.message === 'string' ? errorBody.message : undefined;
        } catch {
            serverMessage = undefined;
        }

        const error: ThreeDxProjectRequestError = new Error(serverMessage ?? `Project request failed with status ${response.status}`);
        error.status = response.status;
        throw error;
    }

    const data = await response.json() as ThreeDxProjectResponse;
    return data.data?.[0] ? data : null;
};

export interface ThreeDxImportResult {
    projectId: string;
    message: string;
}

export const importThreeDxProject = async (project: ThreeDxProject): Promise<ThreeDxImportResult> => {
    const oktaUser = await oktaService.getUser();
    if (!oktaUser?.access_token) {
        throw new Error('No access token is available for the project import request');
    }
    
    const useCurrentUserGPID = resolveConfigValue(
        getRuntimeConfigValue('USE_CURRENT_USER_GPID'),
        import.meta.env.VITE_USE_CURRENT_USER_GPID
    )?.toLowerCase() === 'true';

    if (useCurrentUserGPID) {
        const authenticatedUserGpid = getUserGpid(oktaUser.profile);
        const projectOwner = resolveConfigValue(
            getRuntimeConfigValue('GPID_OWNER'),
            import.meta.env.VITE_GPID_OWNER,
            authenticatedUserGpid
        );
        const projectUser = resolveConfigValue(
            getRuntimeConfigValue('GPID_USER'),
            import.meta.env.VITE_GPID_USER,
            authenticatedUserGpid
        );

        if (!projectOwner || !projectUser) {
            throw new Error('No project GPID is available for the project import request');
        }

        project.project_info = project.project_info ?? {};
        project.project_info.createdBy = projectOwner;
        project.project_info.project_users = [projectUser];
    }
 
    const response = await fetch(threeDxProjectImportUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
            Authorization: `Bearer ${oktaUser.access_token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(project),
    });

    if (!response.ok) {
        let serverMessage: string | undefined;
        try {
            const errorText = await response.text();
            try {
                const errorBody = JSON.parse(errorText);
                serverMessage = typeof errorBody?.message === 'string' ? errorBody.message : undefined;
            } catch {
                serverMessage = errorText || undefined;
            }
        } catch {
            serverMessage = undefined;
        }

        const error: ThreeDxProjectRequestError = new Error(serverMessage ?? `Project import request failed with status ${response.status}`);
        error.status = response.status;
        throw error;
    }

    const responseText = await response.text();
    // Format: "<status>:<projectId>:<message>" - message may itself contain ':'
    const [, projectId = '', ...messageParts] = responseText.split(':');
    return { projectId: projectId.trim(), message: messageParts.join(':').trim() };

};
